import React, { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs'
import { LanguageSensitiveLazySvg } from '@/Components/custom/lazy-svg'
import RenderSafeHtml from './render-safe-html'
import useLocalizedValue from '@/hooks/use-localized-value'
import { cn } from '@/lib/utils'
import { Translatable } from '@/types'
import SimpleSection from '@/Components/custom/simple-section'
import type { Bundle } from '@/types'
import FAQSection from '@/Components/sections/faqs-section'
import TelecomPlansGrid1 from './dynamic-island'
import LoadSerachable from '../sections/load-searchable'
import { useTranslation } from 'react-i18next'
interface FAQItem {
  question: string | null
  answer: string | null
}

interface TabData {
  id: string
  name: Translatable<string>
  icon: string
  richContent?: Translatable<string>
  title?: null | Translatable<string>
  subtitle?: null | Translatable<string>
  plans?: Bundle[]
  tablist?: string
  questions?: Translatable<FAQItem[]>
  faq_icon?: string | null
}
interface TabbedSectionProps {
  tabs: TabData[]
  title?: null | Translatable<string>
  subtitle?: null | Translatable<string>
  className?: null | string
}

const TabTitles: React.FC<TabbedSectionProps> = React.memo(({ tabs }) => {
  return (
    <TabsList className="flex h-fit flex-row gap-2 overflow-x-auto sm:h-full md:gap-6 rtl:flex-row-reverse">
      {tabs.map((tab) => (
        <TabsTrigger
          key={tab.id}
          value={tab.id}
          className={cn(
            'group inline-flex h-[100px] w-[130px] cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl border border-border p-4 text-center text-foreground transition-colors duration-500 hover:border-primary hover:bg-primary hover:text-white sm:p-6 md:h-[140px] md:w-[158px] md:rounded-3xl',
            'data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow'
          )}
        >
          <div className="flex flex-col items-center gap-2 sm:gap-4">
            <LanguageSensitiveLazySvg
              containsPath={true}
              name={tab.icon}
              className={cn(
                'size-6 transition-colors sm:size-8 md:size-10',
                'text-primary group-hover:text-primary-foreground',
                'dark:text-primary',
                'group-data-[state=active]:text-primary-foreground'
              )}
            />
            <div className="text-wrap text-xs font-medium capitalize sm:text-sm sm:font-semibold">
              {useLocalizedValue(tab.name)}
            </div>
          </div>
        </TabsTrigger>
      ))}
    </TabsList>
  )
})

TabTitles.displayName = 'TabTitles'

const TabbedSection: React.FC<TabbedSectionProps> = ({ tabs, className }) => {
  const { i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.id || '')
  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId)
    },
    [tabs]
  )

  if (!tabs || tabs.length === 0) {
    return <div>No Data Provided for the current page.</div>
  }

  return (
    <div
      className={cn('mx-auto flex w-full flex-col pb-6 md:relative md:-top-[1.5rem]', className)}
      dir={i18n.dir()}
    >
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabTitles tabs={tabs} />

        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="py-2 md:py-6">
            <SimpleSection
              top={true}
              title={useLocalizedValue(tab.title || {})}
              subtitle={useLocalizedValue(tab.subtitle || {})}
              className="my-4"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={cn(
                'spacy-y-4 justify-center md:space-x-4 md:space-y-0 rtl:space-y-reverse rtl:md:space-x-reverse'
              )}
            >
              {tab.plans && (
                <TelecomPlansGrid1
                  bundles={tab.plans}
                  className={cn(
                    'grid gap-4',
                    tab.plans && tab.plans.length > 3
                      ? 'grid-cols-[repeat(auto-fit,minmax(280px,1fr))]'
                      : 'grid-cols-[repeat(auto-fit,minmax(280px,350px))]'
                  )}
                />
              )}
              {tab.richContent && (
                <div dir={i18n.dir()}>
                  <RenderSafeHtml
                    className={cn(
                      'prose prose-base max-w-none dark:prose-invert prose-h2:text-foreground prose-p:text-body prose-strong:text-foreground prose-li:marker:text-foreground',
                      'prose-h4:leading-tight prose-h5:mb-0.5 prose-h5:font-medium prose-p:leading-snug prose-h5:md:mb-0 prose-h5:md:text-lg [&>p>span>small]:text-[16px]'
                    )}
                    content={useLocalizedValue(tab.richContent) || ''}
                  />
                </div>
              )}
              {tab.tablist === 'faqs' && tab.questions && (
                <FAQSection questions={tab.questions} icon={tab.faq_icon} adjustGap={false} />
              )}
              {tab.tablist === 'searchable' && <LoadSerachable />}
            </motion.div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
export default TabbedSection
